import { useEffect, useRef } from "react";
import Intercom, {
  update as updateIntercomConnection,
} from "@intercom/messenger-js-sdk";
import { parseJwt } from "../util/util";
import { getIntercomToken } from "../data/api/intercom";

interface UseIntercomParams {
  email?: string;
  name?: string;
  companyId?: string;
}

const INTERCOM_APP_ID = "busoygs4";
const INTERCOM_REGION = "eu";
const DEFAULT_TOKEN_LIFETIME_MS = 2 * 60 * 60 * 1000;
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;
const MIN_TOKEN_REFRESH_DELAY_MS = 5 * 1000;

const getTokenRefreshDelay = (token: string) => {
  const fallbackDelay = DEFAULT_TOKEN_LIFETIME_MS - TOKEN_REFRESH_BUFFER_MS;

  try {
    const { exp } = parseJwt(token);

    if (!exp) return fallbackDelay;

    return Math.max(
      exp * 1000 - Date.now() - TOKEN_REFRESH_BUFFER_MS,
      MIN_TOKEN_REFRESH_DELAY_MS,
    );
  } catch {
    return fallbackDelay;
  }
};

export const useIntercom = ({ companyId, email, name }: UseIntercomParams) => {
  const isBooted = useRef(false);

  useEffect(() => {
    if (!companyId || !email || !name) {
      return;
    }

    let isCancelled = false;
    let refreshTimeout: ReturnType<typeof setTimeout>;

    const bootSecureIntercomConnection = async () => {
      try {
        const token = await getIntercomToken();

        if (isCancelled) return;

        if (isBooted.current) {
          updateIntercomConnection({ intercom_user_jwt: token });
        } else {
          Intercom({
            app_id: INTERCOM_APP_ID,
            region: INTERCOM_REGION,
            name,
            intercom_user_jwt: token,
          });
          isBooted.current = true;
        }

        refreshTimeout = setTimeout(() => {
          void bootSecureIntercomConnection();
        }, getTokenRefreshDelay(token));
      } catch {
        console.error("Fetching of the Intercom token failed.");
      }
    };

    bootSecureIntercomConnection();

    return () => {
      isCancelled = true;
      clearTimeout(refreshTimeout);
    };
  }, [companyId, email, name]);
};
